import type { Entity, Persisted } from '@tunasong/schemas'
import { Button, DndList, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'
import styles from './unfiled-section.module.css'

/** Rows shown before "View all" */
const unfiledCap = 3

export interface UnfiledSectionProps {
  /** My top-level entities that are not spaces */
  entities: Persisted<Entity>[]
}

/**
 * Home aside: items not in a space yet (was "Inbox"; the name implied incoming items). Bounded to three rows
 * with an in-place "View all"; hidden when empty. Rows keep the entity menu (Move…) and can be dragged onto a
 * space row to file them. The rows keep their actions button on touch screens, where lists otherwise drop it.
 */
export const UnfiledSection: FC<UnfiledSectionProps> = props => {
  const { entities } = props
  const [expanded, setExpanded] = useState(false)

  const items = entities.filter(e => !e.trash)
  if (items.length === 0) {
    return null
  }
  const visible = expanded ? items : items.slice(0, unfiledCap)

  return (
    <section className={styles.section} aria-labelledby="home-unfiled">
      <div>
        <Typography id="home-unfiled" variant="h2" className={styles.heading}>
          Unfiled <span className={styles.count}>{items.length}</span>
        </Typography>
        <Typography variant="caption" component="p" className={styles.caption}>
          Not in a space yet
        </Typography>
      </div>
      <DndList
        id="home-unfiled-list"
        entities={visible}
        optionsKey="root"
        itemLayout="compact"
        /* Move… must not hide behind a long press on phones (Decision 7) */
        alwaysShowItemActions
        placeholder="Nothing unfiled"
      />
      {items.length > unfiledCap ? (
        <Button
          className={styles.toggle}
          aria-expanded={expanded}
          aria-controls="home-unfiled-list"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show fewer' : 'View all'}
        </Button>
      ) : null}
    </section>
  )
}

export default UnfiledSection
